import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useMountEffect } from '@modernloop/shared/hooks';
import { v4 as uuid } from 'uuid';
import type { Result } from 'neverthrow';

import type { AgentChatItem } from './agent/chatItems';
import { getAgentThreadHistory, streamAgentTurn } from './agent/agentClient';
import type { AgentTokenProvider } from './agent/agentClient';
import type { AgentThreadSummary } from './agent/protocol';
import useAgentChatStream from './agent/useAgentChatStream';
import useAgentDelegationToken from './agent/useAgentDelegationToken';
import {
  useAgentThreadHistory,
  useAgentThreadList,
  useInvalidateAgentThreadHistory,
  useRefreshAgentThreadList,
  useAgentThreadRename,
} from './agent/useAgentThreads';
import type { useAgentThreadsQuery } from 'src/generated/mloop-graphql';
import type { AgentThreadRenameFailure } from './agent/useAgentThreads';
import { loadPersistedAgentChatSession, savePersistedAgentChatSession } from './agentChatSessionStorage';

type ActiveAgentThread = {
  threadId: string;
  /** A persisted transcript can safely load because this browser session has no live copy of it. */
  shouldLoadHistory: boolean;
  /** The server has accepted a turn for this thread, so reload can return to it. */
  isServerPersisted: boolean;
};

/** A dock surface is local presentation state for a persisted opaque Agent thread id. */
export type AgentDockTab = {
  threadId: string;
  presentation: AgentDockPresentation;
  hasUnread: boolean;
};

export const AGENT_DOCK_PRESENTATION = {
  MINIMIZED: 'MINIMIZED',
  RESTORED: 'RESTORED',
} as const;

export type AgentDockPresentation = (typeof AGENT_DOCK_PRESENTATION)[keyof typeof AGENT_DOCK_PRESENTATION];

export const AGENT_SURFACE = {
  /** No visual consumer currently renders the shared Agent transcript. */
  UNOWNED: 'UNOWNED',
  DOCK: 'DOCK',
  FULL_PAGE: 'FULL_PAGE',
  SIDE_PANEL: 'SIDE_PANEL',
} as const;

/** Semantic state for the one visual consumer permitted to render the shared Agent transcript. */
export type AgentSurfaceState = (typeof AGENT_SURFACE)[keyof typeof AGENT_SURFACE];

/** A mounted presentation surface that can claim or release visual transcript ownership. */
export type AgentSurface = Exclude<AgentSurfaceState, typeof AGENT_SURFACE.UNOWNED>;

const AGENT_SURFACE_FALLBACK_PRIORITY: readonly AgentSurface[] = [AGENT_SURFACE.FULL_PAGE, AGENT_SURFACE.SIDE_PANEL];

/** The single agent chat session consumed by the side panel, full-page, dock, and pop-out surfaces. */
export type AgentChatSession = {
  threadId: string;
  threads: readonly AgentThreadSummary[];
  historyItems: readonly AgentChatItem[];
  liveItems: readonly AgentChatItem[];
  items: readonly AgentChatItem[];
  isHistoryLoading: boolean;
  /** True until the active persisted transcript has settled, including cache-backed refetches. */
  isHistoryPending: boolean;
  isStreaming: boolean;
  /** True after the server has accepted a turn for the active thread. */
  isServerPersisted: boolean;
  /** True once the active thread has any live transcript or an in-flight turn. */
  hasStartedConversation: boolean;
  activeSurface: AgentSurfaceState;
  /** The one unsent composer draft, owned here so it survives a surface transfer. */
  draft: string;
  setDraft: (draft: string) => void;
  sendMessage: (message: string, source?: AgentSurface) => Promise<void>;
  stopStreaming: () => void;
  selectThread: (threadId: string, source?: AgentSurface) => void;
  newThread: (source?: AgentSurface) => void;
  registerSurface: (surface: AgentSurface) => void;
  openThread: (source?: AgentSurface) => void;
  releaseSurface: (surface: AgentSurface) => void;
  dockTabs: readonly AgentDockTab[];
  restoreDockThread: (threadId: string) => void;
  minimizeDockThread: (threadId: string) => void;
  closeDockThread: (threadId: string) => void;
  renameThread: (threadId: string, title: string) => Promise<Result<void, AgentThreadRenameFailure>>;
};

const AgentChatSessionContext = createContext<AgentChatSession | null>(null);

const newAgentThread = (): ActiveAgentThread => ({
  threadId: uuid(),
  shouldLoadHistory: false,
  isServerPersisted: false,
});

const agentThreadFromRoute = (routeThreadId: string | null | undefined): ActiveAgentThread =>
  routeThreadId ? { threadId: routeThreadId, shouldLoadHistory: true, isServerPersisted: true } : newAgentThread();

const agentRouteBindingKey = (isFullPageRoute: boolean, routeThreadId: string | null | undefined): string =>
  isFullPageRoute ? (routeThreadId ?? '__new__') : 'off-route';

/**
 * Remounts on Agent route changes so the shared session rebinds without a route-sync effect.
 * Dashboard routes keep one stable key so dock and side-panel state survive navigation.
 */
const AgentRouteThreadBinding: React.FC<{
  currentThreadId: string;
  isFullPageRoute: boolean;
  isServerPersisted: boolean;
  onBindRouteThread: (thread: ActiveAgentThread) => void;
  routeThreadId: string | null | undefined;
}> = ({ currentThreadId, isFullPageRoute, isServerPersisted, onBindRouteThread, routeThreadId }) => {
  useMountEffect(() => {
    if (!isFullPageRoute) {
      return;
    }
    if (routeThreadId) {
      if (routeThreadId === currentThreadId) {
        return;
      }
      onBindRouteThread(agentThreadFromRoute(routeThreadId));
      return;
    }
    if (!isServerPersisted) {
      return;
    }
    onBindRouteThread(newAgentThread());
  });
  return null;
};

const presentationForSurface = (source: AgentSurface): AgentDockPresentation =>
  source === AGENT_SURFACE.DOCK ? AGENT_DOCK_PRESENTATION.RESTORED : AGENT_DOCK_PRESENTATION.MINIMIZED;

const MAX_DOCK_TABS = 3;

const mostRecentDockTabs = (tabs: readonly AgentDockTab[], activeThreadId?: string | null): AgentDockTab[] => {
  const activeTab = tabs.find((tab) => tab.threadId === activeThreadId);
  const tabsByRecency = activeTab ? [...tabs.filter((tab) => tab.threadId !== activeThreadId), activeTab] : tabs;
  return tabsByRecency.slice(-MAX_DOCK_TABS);
};

const registerDockTab = (tabs: readonly AgentDockTab[], threadId: string, source: AgentSurface): AgentDockTab[] => {
  const existingTab = tabs.find((tab) => tab.threadId === threadId);
  const otherTabs = tabs
    .filter((tab) => tab.threadId !== threadId)
    .map((tab) =>
      source === AGENT_SURFACE.DOCK && tab.presentation === AGENT_DOCK_PRESENTATION.RESTORED
        ? { ...tab, presentation: AGENT_DOCK_PRESENTATION.MINIMIZED }
        : tab
    );
  const openedTab = {
    ...existingTab,
    threadId,
    presentation: presentationForSurface(source),
    // Every source is a visual surface that now shows this transcript, so the thread is read.
    hasUnread: false,
  };
  // The dock is an MRU list. The explicit three-thread limit keeps its controls on one row.
  return mostRecentDockTabs([...otherTabs, openedTab]);
};

/**
 * Owns the dashboard-lifetime AgentChat controller shared by every visual chat surface.
 *
 * Persisted history and the current live transcript stay separate: active history is never
 * refetched while a streamed turn is displayed, preventing the same persisted turn from rendering
 * twice. History is instead invalidated when the user leaves a thread.
 */
export const AgentChatSessionProvider: React.FC<
  React.PropsWithChildren<{
    isAgentEnabled?: boolean;
    /** True when the already-loaded route boundary is waiting for the lazy full-page consumer. */
    isFullPageRoute?: boolean;
    /** The parsed full-page route boundary: undefined off Agent routes, null for a new chat. */
    routeThreadId?: string | null;
    /** Optional clients keep transport tests independent of module mocking. */
    getHistory?: typeof getAgentThreadHistory;
    getToken?: AgentTokenProvider;
    streamTurn?: typeof streamAgentTurn;
    /** Injectable GraphQL thread-list hook for faithful provider tests. */
    useThreadListQuery?: typeof useAgentThreadsQuery;
    /** Injectable list refresh keeps provider tests on the same list-refresh path as production. */
    refreshThreadList?: () => void;
  }>
> = ({
  children,
  getHistory = getAgentThreadHistory,
  getToken: providedGetToken,
  isAgentEnabled = true,
  isFullPageRoute = false,
  routeThreadId,
  refreshThreadList: providedRefreshThreadList,
  streamTurn = streamAgentTurn,
  useThreadListQuery,
}) => {
  const delegatedGetToken = useAgentDelegationToken();
  const getToken = providedGetToken ?? delegatedGetToken;
  const defaultRefreshThreadList = useRefreshAgentThreadList();
  const refreshThreadList = providedRefreshThreadList ?? defaultRefreshThreadList;
  const renameThread = useAgentThreadRename();
  const invalidateThreadHistory = useInvalidateAgentThreadHistory();
  const [persistedSession] = useState(loadPersistedAgentChatSession);
  const persistedDockTabs = useMemo(
    () => mostRecentDockTabs(persistedSession?.dockTabs ?? [], persistedSession?.activeThreadId),
    [persistedSession]
  );
  const [hasReconciledStorage, setHasReconciledStorage] = useState(false);
  const hasReconciledStorageRef = useRef(false);
  const [{ threadId, shouldLoadHistory, isServerPersisted }, setActiveThread] = useState(() =>
    agentThreadFromRoute(routeThreadId)
  );
  const [dockTabs, setDockTabs] = useState<readonly AgentDockTab[]>(persistedDockTabs);
  const [draft, setDraft] = useState('');
  const [activeSurface, setActiveSurface] = useState<AgentSurfaceState>(AGENT_SURFACE.UNOWNED);
  const activeSurfaceRef = useRef<AgentSurfaceState>(AGENT_SURFACE.UNOWNED);
  const mountedSurfacesRef = useRef<Set<AgentSurface>>(new Set());
  const deferredDockRestoreThreadIdRef = useRef<string | null>(null);
  const wasFullPageRouteRef = useRef(isFullPageRoute);
  const hasUserChangedSessionRef = useRef(routeThreadId !== undefined);
  // This closes the gap between selecting a persisted thread and React Query publishing its loading
  // state. The prompt requires the server transcript to be authoritative before a live turn starts.
  const pendingHistoryThreadIdRef = useRef<string | null>(null);
  const [pendingHistoryThreadId, setPendingHistoryThreadId] = useState<string | null>(null);
  const setPendingHistoryThread = useCallback((nextThreadId: string | null) => {
    // The state lets all composers render the provider's gate; the ref preserves that gate before
    // React publishes the state update after a thread switch or cached-history refetch.
    pendingHistoryThreadIdRef.current = nextThreadId;
    setPendingHistoryThreadId(nextThreadId);
  }, []);

  const handleTurnSettled = useCallback(
    (didPersistTurn: boolean) => {
      refreshThreadList();
      if (didPersistTurn) {
        setActiveThread((previousThread) =>
          previousThread.threadId === threadId ? { ...previousThread, isServerPersisted: true } : previousThread
        );
      }
      setDockTabs((previousTabs) =>
        previousTabs.map((tab) =>
          tab.threadId === threadId &&
          tab.presentation === AGENT_DOCK_PRESENTATION.MINIMIZED &&
          activeSurfaceRef.current !== AGENT_SURFACE.FULL_PAGE &&
          activeSurfaceRef.current !== AGENT_SURFACE.SIDE_PANEL
            ? { ...tab, hasUnread: true }
            : tab
        )
      );
    },
    [refreshThreadList, threadId]
  );

  const {
    items: liveItems,
    isStreaming,
    sendMessage: sendMessageFromStream,
    stopStreaming,
    resetTranscript,
  } = useAgentChatStream({ threadId, getToken, onTurnSettled: handleTurnSettled, streamTurn });
  const handleHistoryLoadSettled = useCallback(
    (settledThreadId: string) => {
      if (pendingHistoryThreadIdRef.current === settledThreadId) {
        setPendingHistoryThread(null);
      }
    },
    [setPendingHistoryThread]
  );
  const { historyItems, isLoading: isHistoryLoading } = useAgentThreadHistory(
    threadId,
    getToken,
    isAgentEnabled && shouldLoadHistory,
    handleHistoryLoadSettled,
    getHistory
  );
  const threadList = useAgentThreadList(isAgentEnabled, useThreadListQuery);
  const { threads } = threadList;

  // Browser storage is an external boundary. Reconcile its initial snapshot exactly once against a
  // successful server list; later list refreshes must preserve live dock state and active transcripts.
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Synchronizes versioned browser storage with server-owned thread ids.
  useEffect(() => {
    if (!isAgentEnabled || hasReconciledStorageRef.current || threadList.status !== 'SUCCESS') {
      return;
    }
    hasReconciledStorageRef.current = true;
    if (persistedSession) {
      const serverThreadIds = new Set(threads.map((thread) => thread.externalThreadId));
      const persistedTabIds = new Set(persistedSession.dockTabs.map((tab) => tab.threadId));
      const validPersistedTabs = mostRecentDockTabs(
        persistedSession.dockTabs.filter((tab) => serverThreadIds.has(tab.threadId)),
        persistedSession.activeThreadId
      );
      // Initial reconciliation may remove server-deleted descriptors, but must not overwrite work
      // performed while the list was pending: its live transcript belongs to the current thread.
      setDockTabs((previousTabs) =>
        hasUserChangedSessionRef.current
          ? mostRecentDockTabs(
              previousTabs.filter((tab) => !persistedTabIds.has(tab.threadId) || serverThreadIds.has(tab.threadId))
            )
          : validPersistedTabs
      );
      if (
        !hasUserChangedSessionRef.current &&
        persistedSession.activeThreadId &&
        serverThreadIds.has(persistedSession.activeThreadId)
      ) {
        const activeDockTab = validPersistedTabs.find((tab) => tab.threadId === persistedSession.activeThreadId);
        setPendingHistoryThread(persistedSession.activeThreadId);
        setActiveThread({
          threadId: persistedSession.activeThreadId,
          shouldLoadHistory: true,
          isServerPersisted: true,
        });
        if (
          activeDockTab?.presentation === AGENT_DOCK_PRESENTATION.RESTORED &&
          activeSurfaceRef.current === AGENT_SURFACE.UNOWNED
        ) {
          if (isFullPageRoute) {
            deferredDockRestoreThreadIdRef.current = persistedSession.activeThreadId;
          } else {
            activeSurfaceRef.current = AGENT_SURFACE.DOCK;
            setActiveSurface(AGENT_SURFACE.DOCK);
          }
        }
      }
    }
    setHasReconciledStorage(true);
  }, [isAgentEnabled, isFullPageRoute, persistedSession, setPendingHistoryThread, threadList.status, threads]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Synchronizes the lazy route reservation with its still-unowned persisted dock surface.
  useEffect(() => {
    const didFullPageRouteReservationEnd = wasFullPageRouteRef.current && !isFullPageRoute;
    wasFullPageRouteRef.current = isFullPageRoute;
    if (!didFullPageRouteReservationEnd) {
      return;
    }

    const deferredThreadId = deferredDockRestoreThreadIdRef.current;
    deferredDockRestoreThreadIdRef.current = null;
    const deferredDockTab = dockTabs.find((tab) => tab.threadId === deferredThreadId);
    if (
      deferredThreadId !== threadId ||
      deferredDockTab?.presentation !== AGENT_DOCK_PRESENTATION.RESTORED ||
      activeSurfaceRef.current !== AGENT_SURFACE.UNOWNED
    ) {
      return;
    }

    // The lazy route never mounted, so return only its original persisted dock tab without reconciling storage again.
    activeSurfaceRef.current = AGENT_SURFACE.DOCK;
    setActiveSurface(AGENT_SURFACE.DOCK);
  }, [dockTabs, isFullPageRoute, threadId]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Persists only typed session state through the browser-storage adapter.
  useEffect(() => {
    if (isAgentEnabled && hasReconciledStorage) {
      savePersistedAgentChatSession(dockTabs, isServerPersisted ? threadId : null);
    }
  }, [dockTabs, hasReconciledStorage, isAgentEnabled, isServerPersisted, threadId]);

  const leaveActiveThread = useCallback(() => {
    // Surfaces may unmount independently while a turn is active; only a session action or provider
    // teardown may abort it, so dock/full-page/pop-out consumers continue the same SSE connection.
    invalidateThreadHistory(threadId);
    resetTranscript();
  }, [invalidateThreadHistory, resetTranscript, threadId]);

  const bindRouteThread = useCallback(
    (boundThread: ActiveAgentThread) => {
      hasUserChangedSessionRef.current = true;
      if (boundThread.threadId === threadId) {
        return;
      }
      leaveActiveThread();
      setDraft('');
      setPendingHistoryThread(boundThread.shouldLoadHistory ? boundThread.threadId : null);
      setActiveThread(boundThread);
      activeSurfaceRef.current = AGENT_SURFACE.FULL_PAGE;
      setActiveSurface(AGENT_SURFACE.FULL_PAGE);
      setDockTabs((previousTabs) => registerDockTab(previousTabs, boundThread.threadId, AGENT_SURFACE.FULL_PAGE));
    },
    [leaveActiveThread, setPendingHistoryThread, threadId]
  );

  const selectThread = useCallback(
    (nextThreadId: string, source: AgentSurface = AGENT_SURFACE.DOCK) => {
      hasUserChangedSessionRef.current = true;
      if (nextThreadId === threadId) {
        activeSurfaceRef.current = source;
        setActiveSurface(source);
        setDockTabs((previousTabs) => registerDockTab(previousTabs, nextThreadId, source));
        return;
      }
      // A dock focus is a canonical thread switch, so it deliberately keeps the established stream-cancel behavior.
      leaveActiveThread();
      setDraft('');
      setPendingHistoryThread(nextThreadId);
      setActiveThread({ threadId: nextThreadId, shouldLoadHistory: true, isServerPersisted: true });
      activeSurfaceRef.current = source;
      setActiveSurface(source);
      setDockTabs((previousTabs) => registerDockTab(previousTabs, nextThreadId, source));
    },
    [leaveActiveThread, setPendingHistoryThread, threadId]
  );

  const newThread = useCallback(
    (source: AgentSurface = AGENT_SURFACE.DOCK) => {
      hasUserChangedSessionRef.current = true;
      leaveActiveThread();
      const nextThread = newAgentThread();
      setDraft('');
      setPendingHistoryThread(null);
      setActiveThread(nextThread);
      activeSurfaceRef.current = source;
      setActiveSurface(source);
      setDockTabs((previousTabs) => registerDockTab(previousTabs, nextThread.threadId, source));
    },
    [leaveActiveThread, setPendingHistoryThread]
  );

  const openThread = useCallback(
    (source: AgentSurface = AGENT_SURFACE.DOCK) => {
      if (source === AGENT_SURFACE.FULL_PAGE) {
        deferredDockRestoreThreadIdRef.current = null;
      }
      activeSurfaceRef.current = source;
      setActiveSurface(source);
      setDockTabs((previousTabs) => registerDockTab(previousTabs, threadId, source));
    },
    [threadId]
  );

  const sendMessage = useCallback(
    async (message: string, source: AgentSurface = AGENT_SURFACE.DOCK) => {
      if (!isAgentEnabled || isHistoryLoading || pendingHistoryThreadIdRef.current === threadId) {
        return;
      }
      // A first turn must become dock-reachable before streaming starts, even if its initiating surface closes.
      hasUserChangedSessionRef.current = true;
      openThread(source);
      await sendMessageFromStream(message);
    },
    [isAgentEnabled, isHistoryLoading, openThread, sendMessageFromStream, threadId]
  );

  const releaseActiveSurface = useCallback((surface: AgentSurface) => {
    if (activeSurfaceRef.current === surface) {
      // Preserve the established full-page priority, then keep a mounted side panel visible after dock release.
      const nextSurface =
        AGENT_SURFACE_FALLBACK_PRIORITY.find((candidate) => mountedSurfacesRef.current.has(candidate)) ??
        AGENT_SURFACE.UNOWNED;
      activeSurfaceRef.current = nextSurface;
      setActiveSurface(nextSurface);
    }
  }, []);

  const registerSurface = useCallback((surface: AgentSurface) => {
    mountedSurfacesRef.current.add(surface);
  }, []);

  const releaseSurface = useCallback(
    (surface: AgentSurface) => {
      mountedSurfacesRef.current.delete(surface);
      releaseActiveSurface(surface);
    },
    [releaseActiveSurface]
  );

  const restoreDockThread = useCallback(
    (nextThreadId: string) => {
      selectThread(nextThreadId, AGENT_SURFACE.DOCK);
    },
    [selectThread]
  );

  const minimizeDockThread = useCallback(
    (nextThreadId: string) => {
      setDockTabs((previousTabs) =>
        previousTabs.map((tab) =>
          tab.threadId === nextThreadId ? { ...tab, presentation: AGENT_DOCK_PRESENTATION.MINIMIZED } : tab
        )
      );
      if (nextThreadId === threadId) {
        releaseActiveSurface(AGENT_SURFACE.DOCK);
      }
    },
    [releaseActiveSurface, threadId]
  );

  const closeDockThread = useCallback(
    (nextThreadId: string) => {
      // Closing a dock tab is intentionally presentation-only: thread history remains server persisted.
      setDockTabs((previousTabs) => previousTabs.filter((tab) => tab.threadId !== nextThreadId));
      if (nextThreadId === threadId) {
        releaseActiveSurface(AGENT_SURFACE.DOCK);
      }
    },
    [releaseActiveSurface, threadId]
  );

  const items = useMemo<readonly AgentChatItem[]>(() => [...historyItems, ...liveItems], [historyItems, liveItems]);
  const isHistoryPending = isHistoryLoading || pendingHistoryThreadId === threadId;
  const hasStartedConversation = isServerPersisted || isStreaming || liveItems.length > 0;
  const session = useMemo<AgentChatSession>(
    () => ({
      threadId,
      threads,
      historyItems,
      liveItems,
      items,
      isHistoryLoading,
      isHistoryPending,
      isStreaming,
      isServerPersisted,
      hasStartedConversation,
      activeSurface,
      draft,
      setDraft,
      sendMessage,
      stopStreaming,
      selectThread,
      newThread,
      registerSurface,
      openThread,
      releaseSurface,
      dockTabs,
      restoreDockThread,
      minimizeDockThread,
      closeDockThread,
      renameThread,
    }),
    [
      historyItems,
      isHistoryLoading,
      isHistoryPending,
      isStreaming,
      isServerPersisted,
      hasStartedConversation,
      activeSurface,
      draft,
      items,
      liveItems,
      dockTabs,
      closeDockThread,
      renameThread,
      minimizeDockThread,
      newThread,
      openThread,
      registerSurface,
      releaseSurface,
      restoreDockThread,
      selectThread,
      sendMessage,
      stopStreaming,
      threadId,
      threads,
    ]
  );

  return (
    <AgentChatSessionContext.Provider value={session}>
      <AgentRouteThreadBinding
        key={agentRouteBindingKey(isFullPageRoute, routeThreadId)}
        currentThreadId={threadId}
        isFullPageRoute={isFullPageRoute}
        isServerPersisted={isServerPersisted}
        onBindRouteThread={bindRouteThread}
        routeThreadId={routeThreadId}
      />
      {children}
    </AgentChatSessionContext.Provider>
  );
};

/** Returns the dashboard's canonical AgentChat session for a visual chat surface. */
export function useAgentChatSession(): AgentChatSession {
  const session = useContext(AgentChatSessionContext);
  if (!session) {
    throw new Error('useAgentChatSession must be used within AgentChatSessionProvider.');
  }
  return session;
}
